import { Text } from "@/components/podkit/typography/Text";
import IconBubbleText6 from "central-icons/IconBubbleText6";
import IconConsoleSimple from "central-icons/IconConsoleSimple";
import type React from "react";
import { useMemo, useState, type FC } from "react";
import { Button } from "@/components/flexkit/Button";
import { AutomationStepKindPill } from "@/components/automations/AutomationStepKindPill";
import { AutomationStepNode, type AutomationStepNodeVariant } from "@/components/automations/AutomationStepNode";
import { AutomationStepArrow } from "@/components/automations/AutomationStepArrow";
import {
    editableStepsAtom,
    isEditingStepsAtom,
    removeStepAtom,
    stepIndexBeingEditedAtom,
    stepNodeYPositionAtom,
    stepListContainerAtom,
    createNewStepAtom,
    isCreatingStepAtom,
} from "./state";
import type {
    WorkflowEditFormPullRequestStepData,
    WorkflowEditFormStepData,
} from "@/components/automations/automation-edit-form-data";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { useLayoutEffect, useRef, createContext, useContext } from "react";
import { withGitpodSize } from "@/assets/icons/utils";
import {
    DropdownMenu,
    DropdownMenuItem,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { DndContext, DragOverlay, useDraggable, useDroppable, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { useStore } from "jotai";
import { cn } from "@/components/podkit/lib/cn";
import IconCirclePlus from "central-icons/IconCirclePlus";
import { atomEffect } from "jotai-effect";
import IconPullRequest from "central-icons/IconPullRequest";


const IconCirclePlusWithSize = withGitpodSize(IconCirclePlus);

const ActiveDragContext = createContext<string | null>(null);

export const AutomationStepList: FC<{ steps: WorkflowEditFormStepData[] }> = ({ steps }) => {
    const store = useStore();
    const [activeId, setActiveId] = useState<string | null>(null);
    const isEditingSteps = useAtomValue(isEditingStepsAtom);

    // Require 8px of movement before drag starts, allowing clicks to pass through
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
    );

    const onDragStart = (event: DragStartEvent) => {
        setActiveId(event.active.id as string);
    };

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const fromIndex = active.data.current?.stepIndex as number;
        const toIndex = over?.data.current?.stepIndex as number;
        const currentSteps = store.get(editableStepsAtom);
        const currentEditingIndex = store.get(stepIndexBeingEditedAtom);
        
        if (currentSteps && fromIndex !== undefined && toIndex !== undefined && fromIndex !== toIndex) {
            const newSteps = [...currentSteps];
            const [stepToMove] = newSteps.splice(fromIndex, 1);
            // Calculate the adjusted insertion index
            // If moving forward (fromIndex < toIndex), we need to adjust because we removed an item
            const adjustedToIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
            // Insert the item at the new position
            newSteps.splice(adjustedToIndex, 0, stepToMove);
            store.set(editableStepsAtom, newSteps);
            
            // Update the editing index if needed
            if (currentEditingIndex !== null) {
                if (fromIndex === currentEditingIndex) {
                    // The dragged node was being edited - update to its new position
                    store.set(stepIndexBeingEditedAtom, adjustedToIndex);
                } else {
                    // A different node was dragged while edit panel is open
                    // Swap the edit view to the dragged node
                    store.set(stepIndexBeingEditedAtom, adjustedToIndex);
                }
            }
            // If no edit panel was open (currentEditingIndex === null), don't open one
        }
        setActiveId(null);
    };

    const activeStep = activeId ? steps.find((step) => step.id === activeId) : null;
    const activeStepIndex = activeId ? steps.findIndex((step) => step.id === activeId) : -1;

    return (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
            <ActiveDragContext.Provider value={activeId}>
                <ol className="flex min-h-full max-w-[720px] flex-1 flex-col items-center">
                    {isEditingSteps && (
                        <li className="w-full">
                            <AutomationStepDropZone
                                stepIndex={-1}
                                stepId={`dropzone-top-${steps[0].id}`}
                                targetIndex={0}
                                totalSteps={steps.length}
                            />
                        </li>
                    )}
                    {steps.map((step, index) => (
                        <AutomationStepItem
                            key={step.id}
                            step={step}
                            stepIndex={index}
                            isLast={index === steps.length - 1}
                        />
                    ))}
                    {isEditingSteps && (
                        <li className="w-full">
                            <AutomationStepDropZone
                                stepIndex={steps.length - 1}
                                stepId={`dropzone-bottom-${steps[steps.length - 1].id}`}
                                targetIndex={steps.length}
                                totalSteps={steps.length}
                            />
                        </li>
                    )}
                    {isEditingSteps && <NewAutomationStepItem />}
                </ol>
                <DragOverlay
                    dropAnimation={{
                        duration: 300,
                        easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                    }}
                >
                    {activeStep && activeStepIndex !== -1 ? (
                        <DragOverlayContent step={activeStep} stepIndex={activeStepIndex} />
                    ) : null}
                </DragOverlay>
            </ActiveDragContext.Provider>
        </DndContext>
    );
};

/**
 * Hook that handles positioning logic for selected step nodes.
 * When a step is selected, it calculates and updates the Y position
 * relative to the parent container for the edit area alignment.
 *
 * @param stepIndex - The index of the step in the list
 * @returns An object containing:
 *   - `nodeRef`: Ref to attach to the node element
 *   - `isSelected`: Whether this step is currently selected for editing
 */
function useSelectedStepNodePositioning(stepIndex: number) {
    const isSelected = useAtomValue(stepIndexBeingEditedAtom) === stepIndex;
    const setStepNodeYPosition = useSetAtom(stepNodeYPositionAtom);
    const nodeRef = useRef<HTMLDivElement>(null);
    const store = useStore();

    useLayoutEffect(() => {
        if (isSelected && nodeRef.current) {
            const rect = nodeRef.current.getBoundingClientRect();
            const parentContainer = store.get(stepListContainerAtom);
            if (parentContainer) {
                const parentRect = parentContainer.getBoundingClientRect();
                const parentStyles = window.getComputedStyle(parentContainer);
                const paddingTop = Number.parseFloat(parentStyles.paddingTop);
                const yPosition = rect.top - parentRect.top - paddingTop;
                setStepNodeYPosition(yPosition);
            }
        }
    }, [isSelected, setStepNodeYPosition, store, stepIndex]);

    return { nodeRef, isSelected };
}

const AutomationStepItem: FC<{ step: WorkflowEditFormStepData; stepIndex: number; isLast: boolean }> = ({
    step,
    stepIndex,
    isLast,
}) => {
    const isEditingSteps = useAtomValue(isEditingStepsAtom);

    let node: React.ReactNode | null = null;
    switch (step.type) {
        case "command":
            node = <AutomationTaskStepNode content={step.content} editable={isEditingSteps} stepIndex={stepIndex} stepId={step.id} />;
            break;
        case "prompt":
            node = <AutomationAgentStepNode content={step.content} editable={isEditingSteps} stepIndex={stepIndex} stepId={step.id} />;
            break;
        case "pr":
            node = <AutomationStepPullRequestNode data={step} editable={isEditingSteps} stepIndex={stepIndex} stepId={step.id} />;
            break;
        default:
            node = null;
            break;
    }

    if (!node) {
        return null;
    }

    if (isLast) {
        return (
            <li className="w-full">
                <DraggableAutomationStepItem stepIndex={stepIndex} stepId={step.id}>
                    {node}
                </DraggableAutomationStepItem>
            </li>
        );
    }

    return (
        <li className="w-full">
            <DraggableAutomationStepItem stepIndex={stepIndex} stepId={step.id}>
                {node}
            </DraggableAutomationStepItem>
            <AutomationStepDropZone stepIndex={stepIndex} stepId={step.id} />
        </li>
    );
};

const DraggableAutomationStepItem: FC<{ children: React.ReactNode; stepIndex: number; stepId: string }> = ({
    children,
    stepIndex,
    stepId,
}) => {
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const activeId = useContext(ActiveDragContext);
    // Enable dragging when in edit mode (regardless of whether a step is being edited)
    const isDragEnabled = isEditingSteps;
    const { setNodeRef, listeners, attributes } = useDraggable({
        id: stepId,
        disabled: !isDragEnabled,
        data: { stepIndex },
    });

    const isBeingDragged = activeId === stepId;

    return (
        <div
            ref={setNodeRef}
            className={cn("w-full", { "opacity-40": isBeingDragged })}
            {...(isDragEnabled ? listeners : {})}
            {...attributes}
        >
            {children}
        </div>
    );
};

const AutomationStepDropZone: FC<{
    stepIndex: number;
    stepId: string;
    targetIndex?: number;
    totalSteps?: number;
}> = ({ stepIndex, stepId, targetIndex, totalSteps }) => {
    const isEditingSteps = useAtomValue(isEditingStepsAtom);
    const finalTargetIndex = targetIndex !== undefined ? targetIndex : stepIndex + 1;
    const { isOver, setNodeRef, active } = useDroppable({
        id: stepId,
        disabled: !isEditingSteps,
        data: { stepIndex: finalTargetIndex },
    });

    let isDroppable;
    if (isOver && active?.data.current?.stepIndex !== undefined) {
        const activeIndex = active.data.current.stepIndex as number;
        // For top dropzone (targetIndex === 0), allow if not already at index 0
        if (targetIndex === 0) {
            isDroppable = activeIndex !== 0;
        }
        // For bottom dropzone (targetIndex === totalSteps), allow if not already at last index
        else if (targetIndex !== undefined && totalSteps !== undefined && targetIndex === totalSteps) {
            isDroppable = activeIndex !== totalSteps - 1;
        }
        // For middle dropzones, use the existing logic
        else {
            isDroppable = !(activeIndex - 1 === stepIndex || activeIndex === stepIndex);
        }
    } else {
        isDroppable = false;
    }

    const isTopDropzone = targetIndex === 0;
    const isBottomDropzone = targetIndex !== undefined && totalSteps !== undefined && targetIndex === totalSteps;
    const isTopOrBottomDropzone = isTopDropzone || isBottomDropzone;

    return (
        <div
            ref={setNodeRef}
            className={cn("flex w-full flex-row items-center transition-all duration-300 ease-in-out", {
                "pl-6": !isTopOrBottomDropzone,
                "pb-4": isTopDropzone && isDroppable,
                "pt-4 pb-1": isBottomDropzone && isDroppable,
            })}
        >
            {!isTopOrBottomDropzone && (
                <AutomationStepArrow className={cn("-mt-[5.5px] mr-3", { "text-content-brand": isDroppable })} />
            )}
            <hr
                className={cn("h-[1.5px] flex-1 rounded-full border-0 bg-content-brand", {
                    "opacity-0": !isDroppable,
                })}
            />
        </div>
    );
};

const AutomationTaskStepNode: FC<{ content: string; editable: boolean; stepIndex: number; stepId: string }> = ({
    content,
    editable,
    stepIndex,
    stepId,
}) => {
    const { nodeRef, isSelected } = useSelectedStepNodePositioning(stepIndex);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick) {
            setStepIndexBeingEdited(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-task-step" : undefined}
            showDragHandle={editable}
        >
            <AutomationStepKindPill variant="warning" LeadingIcon={IconConsoleSimple}>
                Shell Script
            </AutomationStepKindPill>
            <div className="grid w-full grid-cols-[1fr_auto] items-start gap-2">
                <Text className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border-subtle bg-surface-secondary px-3 py-2 font-mono text-base font-normal">
                    <span className="text-content-muted">$</span> {content}
                </Text>
                {editable ? <AutomationStepNodeEditMenu stepIndex={stepIndex} /> : null}
            </div>
        </AutomationStepNode>
    );
};

const AutomationAgentStepNode: FC<{ content: string; editable: boolean; stepIndex: number; stepId: string }> = ({
    content,
    editable,
    stepIndex,
    stepId,
}) => {
    const { nodeRef, isSelected } = useSelectedStepNodePositioning(stepIndex);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick) {
            setStepIndexBeingEdited(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-prompt-step" : undefined}
            showDragHandle={editable}
        >
            <AutomationStepKindPill variant="brand" LeadingIcon={IconBubbleText6}>
                Prompt
            </AutomationStepKindPill>
            <div className="grid w-full grid-cols-[1fr_auto] items-start gap-2">
                <Text className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border-subtle px-3 py-2 text-base font-normal">
                    {content}
                </Text>
                {editable ? <AutomationStepNodeEditMenu stepIndex={stepIndex} /> : null}
            </div>
        </AutomationStepNode>
    );
};

const AutomationStepPullRequestNode: FC<{
    data: WorkflowEditFormPullRequestStepData;
    editable: boolean;
    stepIndex: number;
    stepId: string;
}> = ({ data, editable, stepIndex, stepId }) => {
    const { nodeRef, isSelected } = useSelectedStepNodePositioning(stepIndex);
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const activeId = useContext(ActiveDragContext);
    const isDragging = activeId === stepId;

    let variant: AutomationStepNodeVariant;
    if (isDragging) {
        variant = "dragging";
    } else if (isSelected) {
        variant = "selected";
    } else if (editable) {
        variant = "editable";
    } else {
        variant = "readOnly";
    }

    const canClick = editable && !isSelected;

    const handleClick = () => {
        if (canClick) {
            setStepIndexBeingEdited(stepIndex);
        }
    };

    return (
        <AutomationStepNode
            ref={nodeRef}
            className={cn("w-full", { "cursor-pointer": canClick })}
            variant={variant}
            onClick={canClick ? handleClick : undefined}
            data-tracking-id={canClick ? "edit-pr-step" : undefined}
            showDragHandle={editable}
        >
            <AutomationStepKindPill variant="success" LeadingIcon={IconPullRequest}>
                {data.isDraft ? "Draft pull request" : "Pull request"}
            </AutomationStepKindPill>
            <div className="grid w-full grid-cols-[1fr_auto] items-start">
                <Text className="self-center font-medium">{data.title}</Text>
                {editable ? <AutomationStepNodeEditMenu stepIndex={stepIndex} /> : null}
                <Text className="text-content-muted">{data.description}</Text>
                <Text className="col-span-2 col-start-1 pt-3 text-content-muted">
                    <span className="text-sm text-content-muted">Branch:</span> {data.branch}
                </Text>
            </div>
        </AutomationStepNode>
    );
};

/**
 * Edit menu for a step node. Inlcudes the button that triggers the edit menu.
 */
const AutomationStepNodeEditMenu: FC<{ stepIndex: number }> = ({ stepIndex }) => {
    const setStepIndexBeingEdited = useSetAtom(stepIndexBeingEditedAtom);
    const removeStep = useSetAtom(removeStepAtom);
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="md"
                    className="self-start"
                    LeadingIcon={IconEllipsisHorizontal}
                    onPointerDown={(e) => {
                        // Prevent drag from starting when clicking the menu trigger
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        // Prevent drag from starting when clicking the menu trigger
                        e.stopPropagation();
                    }}
                >
                    <span className="sr-only">Edit node</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="max-w-40" align="end">
                <DropdownMenuItem
                    onPointerDown={(e) => {
                        // Prevent drag from continuing when interacting with menu items
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        // Prevent drag from continuing when interacting with menu items
                        e.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        setStepIndexBeingEdited(stepIndex);
                    }}
                    data-tracking-id="edit"
                >
                    Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                    variant="destructive"
                    onPointerDown={(e) => {
                        // Prevent drag from continuing when interacting with menu items
                        e.stopPropagation();
                    }}
                    onMouseDown={(e) => {
                        // Prevent drag from continuing when interacting with menu items
                        e.stopPropagation();
                    }}
                    onClick={(event) => {
                        event.stopPropagation();
                        removeStep(stepIndex);
                    }}
                    data-tracking-id="remove-step"
                >
                    Remove step
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const NewAutomationStepItem = () => {
    const isCreatingStep = useAtomValue(isCreatingStepAtom);
    const createNewStep = useSetAtom(createNewStepAtom);
    const setStepNodeYPosition = useSetAtom(stepNodeYPositionAtom);
    const ref = useRef<HTMLLIElement>(null);
    const store = useStore();

    const repositionEffect = useMemo(
        () =>
            atomEffect((get) => {
                // run this effect when the step list changes
                // because the position of this item may have changed
                get(editableStepsAtom);

                // if not creating a step, the editor is not visible
                // so there is no need to reposition
                if (!get.peek(isCreatingStepAtom)) return;

                // effect is run synchronously when an atom is set
                // which means the layout has not been updated yet
                // queue the repositioning to the next tick
                setTimeout(() => {
                    if (!ref.current) return;
                    const rect = ref.current.getBoundingClientRect();
                    const parentContainer = get.peek(stepListContainerAtom);
                    if (parentContainer) {
                        const parentRect = parentContainer.getBoundingClientRect();
                        const yPosition = rect.top - parentRect.top;
                        setStepNodeYPosition(yPosition);
                    }
                }, 0);
            }),
        [setStepNodeYPosition],
    );

    useAtom(repositionEffect);

    return (
        <li ref={ref} className="flex w-full items-center gap-2 pt-3">
            <Button
                variant="primary"
                size="sm"
                LeadingIcon={IconCirclePlusWithSize}
                className={isCreatingStep ? "outline outline-offset-2 outline-border-brand" : ""}
                onClick={() => {
                    if (ref.current) {
                        const rect = ref.current.getBoundingClientRect();
                        const parentContainer = store.get(stepListContainerAtom);
                        if (parentContainer) {
                            const parentRect = parentContainer.getBoundingClientRect();
                            const yPosition = rect.top - parentRect.top;
                            setStepNodeYPosition(yPosition);
                        }
                    }
                    createNewStep();
                }}
                data-tracking-id="step"
            >
                Step
            </Button>
            <hr className={cn("h-[1.5px] flex-1 rounded-full border-0", isCreatingStep ? "bg-border-brand" : "bg-border-strong")} />
        </li>
    );
};

const DragOverlayContent: FC<{ step: WorkflowEditFormStepData; stepIndex: number }> = ({ step, stepIndex }) => {
    let child: React.ReactNode | null = null;
    switch (step.type) {
        case "command":
            child = <AutomationTaskStepNode content={step.content} editable stepIndex={stepIndex} stepId={step.id} />;
            break;
        case "prompt":
            child = <AutomationAgentStepNode content={step.content} editable stepIndex={stepIndex} stepId={step.id} />;
            break;
        case "pr":
            child = <AutomationStepPullRequestNode data={step} editable stepIndex={stepIndex} stepId={step.id} />;
            break;
        default:
            child = null;
            break;
    }
    if (!child) {
        return null;
    }
    return <div className="rounded-lg shadow">{child}</div>;
};
